import React from 'react';
import { Link } from 'react-router-dom';
import { BRAND, IMAGES } from '../lib/content';
import {
  Gift,
  Mail,
  Calendar,
  ShieldCheck,
  RefreshCw,
  Heart,
  HeartPulse,
  Scale,
  Sparkles,
} from 'lucide-react';

const NOTES = [
  {
    icon: Mail,
    title: 'First impressions matter',
    body: (
      <>
        The booking form on my contact page is my preferred way to be reached;
        I&rsquo;ve written it so nothing important is missed. Email or text
        message are also options, just make sure you include your name, desired
        date, duration, location and the kind of experience you&rsquo;re
        dreaming of. I reply personally, and I prioritise quality over quantity.
        Thoughtful messages always come first.
      </>
    ),
  },
  {
    icon: Calendar,
    title: 'Planning ahead is sexy',
    body: (
      <>
        I&rsquo;m rarely available with under 48 hours&rsquo; notice, so please
        plan ahead where you can, both to avoid disappointment, and to let
        anticipation stoke the flames a little. The dates I remember most are
        the ones we&rsquo;ve both been daydreaming about for weeks. Tours in
        particular often book out before I even reach the destination, so if
        you know I&rsquo;m coming to your city, don&rsquo;t leave it too late.
      </>
    ),
  },
  {
    icon: ShieldCheck,
    title: 'Securing our time',
    body: (
      <>
        A 30&ndash;50% deposit is appreciated to secure our booking. My
        preferred methods are <span className="text-zb-ink">Beem</span>{' '}
        (discreet, instant, Australian),{' '}
        <span className="text-zb-ink">Bitcoin</span>, or{' '}
        <span className="text-zb-ink">Wise</span> for international transfers.
        The balance is settled gracefully in cash upon arrival.
      </>
    ),
  },
  {
    icon: RefreshCw,
    title: 'If life gets in the way',
    body: (
      <>
        In the rare instance I need to cancel for any reason, you will receive
        a full refund promptly via Beem. Life happens for you too, and I
        understand. In Melbourne, I&rsquo;m happy to raincheck when given 48
        hours&rsquo; notice. On tour or FMTY, I&rsquo;ll try to reschedule
        wherever possible, but if we can&rsquo;t find a new time, the deposit
        is kept to cover the held slot. For last-minute changes within 48
        hours on tour or FMTY, the full booking fee is owed unless otherwise
        agreed.
      </>
    ),
  },
  {
    icon: HeartPulse,
    title: 'Health matters',
    body: (
      <>
        I prioritise my health and wellbeing, and expect you to do the same.
        As adults, we are each in charge of our own sexual health and the
        health of anyone we&rsquo;re intimate with. Despite adhering to strict
        sexual health practices, I&rsquo;m tested regularly, and I expect you
        to be the same. Oral sex still carries risks, as does protected
        penetration, so please stay educated and stay current with your
        testing. Take your sexual health and overall health seriously. Regular
        dental check-ups go a long way for oral health and hygiene too.
      </>
    ),
  },
  {
    icon: Heart,
    title: 'First time? Breathe.',
    body: (
      <>
        If you&rsquo;re new to this, think of our time together as a first date:
        the kind without the ambiguity, the admin, or the usual mess. Show up
        as yourself. Freshly groomed when you can be, but please don&rsquo;t
        fret. A shower before we get physical is always part of our time
        together, so you&rsquo;ll have a moment to settle, rinse off, and arrive
        in the present with me. A small tip on grooming: please keep pubic
        hair neatly trimmed, but avoid a fresh shave right before we meet.
        Micro-cuts and extra-sensitive skin down there aren&rsquo;t conducive
        to a good time for either of us.
      </>
    ),
  },
  {
    icon: Scale,
    title: 'A few important policies',
    body: (
      <>
        If at any point my health or personal safety feels compromised during
        our time together, I reserve the right to end the session, no
        exceptions. All images, written material and creative content on this
        site are my intellectual property and remain so; unauthorised use,
        reproduction or distribution will be pursued to the full extent of
        the law. I do not consent to any information about our time together,
        my identity, or my personal life being shared on review boards,
        forums, message groups or public sites of any kind. Breach of this
        may result in legal action. Discretion is a two-way street. If you
        ever see me in public, kindly do not approach or acknowledge me.
        We&rsquo;ll always have our private world.
      </>
    ),
  },
  {
    icon: Sparkles,
    title: 'Little ways to make an impression',
    body: (
      <>
        A few small courtesies I always notice: freshly shaved or neatly
        groomed facial hair (my skin is soft and easily marked by sharp stubble),
        fresh breath and good oral hygiene. And if you&rsquo;d like to spoil me,
        my{' '}
        <a
          href={BRAND.wishlist}
          target="_blank"
          rel="noreferrer"
          data-testid="etiquette-wishlist-link"
          className="text-zb-brand underline underline-offset-4 hover:text-zb-brandHover"
        >
          wishlist
        </a>{' '}
        holds all the little things that bring me joy. Flowers, a bottle,
        anything from the wishlist: none of it required, all of it remembered.
      </>
    ),
  },
];

export default function Etiquette() {
  return (
    <div>
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-12">
        <div className="font-label text-zb-accent mb-6">Etiquette</div>
        <h1 className="font-serif text-5xl md:text-7xl text-zb-ink tracking-tight leading-[0.95]">
          A few thoughtful <span className="italic text-zb-brand">notes.</span>
        </h1>
        <p className="mt-8 text-zb-ink2 text-lg max-w-2xl leading-relaxed">
          A little courtesy goes a long way. Everything below is in service of
          making our time together feel effortless, discreet, and deliciously
          mutual.
        </p>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-12 py-8 md:py-16">
        <div className="space-y-5 md:space-y-6">
          {NOTES.map((n, i) => {
            const Icon = n.icon;
            const isAlt = i % 2 === 1;
            return (
              <article
                key={n.title}
                data-testid={`etiquette-item-${i}`}
                className={`relative overflow-hidden border-l-4 border-zb-brand pl-5 md:pl-8 pr-5 md:pr-10 py-7 md:py-10 zb-fade-up ${
                  isAlt ? 'bg-zb-bg2' : 'bg-zb-bg'
                }`}
                style={{ animationDelay: `${i * 0.05}s` }}
              >
                {/* Decorative watermark number */}
                <div
                  aria-hidden
                  className="pointer-events-none absolute -right-4 md:right-4 top-0 md:top-1 font-serif text-[110px] md:text-[150px] leading-none text-zb-brand/[0.06] select-none"
                >
                  {String(i + 1).padStart(2, '0')}
                </div>

                <div className="relative flex flex-col md:flex-row md:items-start md:gap-8">
                  <div className="md:w-40 flex-shrink-0 md:pt-2">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-zb-brand/10 flex items-center justify-center text-zb-brand">
                        {Icon && <Icon size={17} />}
                      </div>
                      <div className="font-label text-zb-accent text-[10.5px] tracking-[0.28em]">
                        No. {String(i + 1).padStart(2, '0')}
                      </div>
                    </div>
                    <h2 className="mt-4 md:mt-5 font-serif text-2xl md:text-3xl text-zb-ink leading-tight">
                      {n.title}
                    </h2>
                  </div>

                  <div className="flex-1 mt-5 md:mt-1 text-zb-ink2 text-[15px] md:text-base leading-[1.75]">
                    {n.body}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* WISHLIST SPOTLIGHT */}
      <section className="bg-zb-bg2 border-y border-zb-border">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-12 md:py-16 text-center">
          <div className="inline-flex items-center gap-2 font-label text-zb-accent mb-5">
            <Gift size={14} /> Wishlist
          </div>
          <h3 className="font-serif text-3xl md:text-5xl text-zb-ink leading-tight">
            Spoil me, <span className="italic text-zb-brand">sweetly.</span>
          </h3>
          <p className="mt-5 text-zb-ink2 text-base max-w-lg mx-auto leading-relaxed">
            Gifts guaranteed to bring me joy. Never expected, always
            appreciated.
          </p>
          <a
            href={BRAND.wishlist}
            target="_blank"
            rel="noreferrer"
            data-testid="etiquette-wishlist-cta"
            className="inline-block mt-8 rounded-full border border-zb-border hover:border-zb-brand text-zb-ink hover:text-zb-brand px-8 py-3.5 text-xs tracking-[0.22em] uppercase transition-colors"
          >
            View my wishlist →
          </a>
        </div>
      </section>

      <section className="relative mt-0">
        <div className="absolute inset-0">
          <img
            src={IMAGES.gallery[0]}
            alt=""
            className="w-full h-full object-cover"
            style={{ filter: 'brightness(0.65)' }}
          />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-22 text-center text-zb-bg">
          <div className="font-label text-zb-bg/80 mb-6">When you&rsquo;re ready</div>
          <h3 className="font-serif text-4xl md:text-6xl leading-tight">
            Shall we <span className="italic">begin?</span>
          </h3>
          <Link
            to="/contact"
            data-testid="etiquette-cta-book"
            className="inline-block mt-10 rounded-full bg-zb-bg hover:bg-zb-brand text-zb-ink hover:text-zb-bg px-10 py-4 text-xs tracking-[0.22em] uppercase transition-colors"
          >
            Let&rsquo;s meet
          </Link>
        </div>
      </section>
    </div>
  );
}
